"use client";

import type React from "react";

import { useEditor, EditorContent, type Editor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Placeholder from "@tiptap/extension-placeholder";
import Link from "@tiptap/extension-link";
import Image from "@tiptap/extension-image";
import Table from "@tiptap/extension-table";
import TableRow from "@tiptap/extension-table-row";
import TableCell from "@tiptap/extension-table-cell";
import TableHeader from "@tiptap/extension-table-header";
import TextAlign from "@tiptap/extension-text-align";
import { useEffect, useState } from "react";
import {
  Bold,
  Italic,
  List,
  Heading1,
  Heading2,
  Code,
  Quote,
  LinkIcon,
  ImageIcon,
  AlignLeft,
  AlignCenter,
  AlignRight,
  Undo,
  Redo,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

interface ToolbarButtonProps {
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
  isActive?: boolean;
}

const ToolbarButton = ({ onClick, icon, label, isActive }: ToolbarButtonProps) => {
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            type="button"
            onClick={onClick}
            className={cn("h-8 w-8 p-0", isActive && "bg-muted text-primary")}
          >
            {icon}
            <span className="sr-only">{label}</span>
          </Button>
        </TooltipTrigger>
        <TooltipContent>
          <p>{label}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

interface MenuBarProps {
  editor: Editor | null;
}

const MenuBar = ({ editor }: MenuBarProps) => {
  if (!editor) {
    return null;
  }

  const addImage = () => {
    const url = window.prompt("URL");
    if (url) {
      editor.chain().focus().setImage({ src: url }).run();
    }
  };

  const setLink = () => {
    const previousUrl = editor.getAttributes("link").href;
    const url = window.prompt("URL", previousUrl);

    // cancelled
    if (url === null) {
      return;
    }

    // empty
    if (url === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }

    // update link
    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  };

  return (
    <div className="flex flex-wrap items-center gap-1 p-1 border-b">
      <ToolbarButton
        onClick={() => editor.chain().focus().toggleBold().run()}
        icon={<Bold className="h-4 w-4" />}
        label="Bold"
        isActive={editor.isActive("bold")}
      />
      <ToolbarButton
        onClick={() => editor.chain().focus().toggleItalic().run()}
        icon={<Italic className="h-4 w-4" />}
        label="Italic"
        isActive={editor.isActive("italic")}
      />

      <Separator orientation="vertical" className="mx-1 h-6" />

      <ToolbarButton
        onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
        icon={<Heading1 className="h-4 w-4" />}
        label="Heading 1"
        isActive={editor.isActive("heading", { level: 1 })}
      />
      <ToolbarButton
        onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
        icon={<Heading2 className="h-4 w-4" />}
        label="Heading 2"
        isActive={editor.isActive("heading", { level: 2 })}
      />

      <Separator orientation="vertical" className="mx-1 h-6" />

      <ToolbarButton
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        icon={<List className="h-4 w-4" />}
        label="Bullet List"
        isActive={editor.isActive("bulletList")}
      />
      <ToolbarButton
        onClick={() => editor.chain().focus().toggleCodeBlock().run()}
        icon={<Code className="h-4 w-4" />}
        label="Code Block"
        isActive={editor.isActive("codeBlock")}
      />
      <ToolbarButton
        onClick={() => editor.chain().focus().toggleBlockquote().run()}
        icon={<Quote className="h-4 w-4" />}
        label="Blockquote"
        isActive={editor.isActive("blockquote")}
      />

      <Separator orientation="vertical" className="mx-1 h-6" />

      <ToolbarButton
        onClick={setLink}
        icon={<LinkIcon className="h-4 w-4" />}
        label="Link"
        isActive={editor.isActive("link")}
      />
      <ToolbarButton onClick={addImage} icon={<ImageIcon className="h-4 w-4" />} label="Image" />
      {/* <ToolbarButton
        onClick={() => editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()}
        icon={<TableIcon className="h-4 w-4" />}
        label="Table"
        isActive={editor.isActive("table")}
      /> */}

      <Separator orientation="vertical" className="mx-1 h-6" />

      <ToolbarButton
        onClick={() => editor.chain().focus().setTextAlign("left").run()}
        icon={<AlignLeft className="h-4 w-4" />}
        label="Align Left"
        isActive={editor.isActive({ textAlign: "left" })}
      />
      <ToolbarButton
        onClick={() => editor.chain().focus().setTextAlign("center").run()}
        icon={<AlignCenter className="h-4 w-4" />}
        label="Align Center"
        isActive={editor.isActive({ textAlign: "center" })}
      />
      <ToolbarButton
        onClick={() => editor.chain().focus().setTextAlign("right").run()}
        icon={<AlignRight className="h-4 w-4" />}
        label="Align Right"
        isActive={editor.isActive({ textAlign: "right" })}
      />

      <div className="ml-auto flex items-center">
        <ToolbarButton
          onClick={() => editor.chain().focus().undo().run()}
          icon={<Undo className="h-4 w-4" />}
          label="Undo"
        />
        <ToolbarButton
          onClick={() => editor.chain().focus().redo().run()}
          icon={<Redo className="h-4 w-4" />}
          label="Redo"
        />
      </div>
    </div>
  );
};

interface RichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
}

export const RichTextEditor = ({
  value,
  onChange,
  placeholder = "Write something...",
  className,
}: RichTextEditorProps) => {
  const [isMounted, setIsMounted] = useState(false);

  const editor = useEditor({
    extensions: [
      StarterKit,
      Placeholder.configure({
        placeholder,
      }),
      Link.configure({
        openOnClick: false,
      }),
      Image,
      Table.configure({
        resizable: true,
      }),
      TableRow,
      TableHeader,
      TableCell,
      TextAlign.configure({
        types: ["heading", "paragraph"],
      }),
    ],
    content: value,
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
    editorProps: {
      attributes: {
        class: "prose-editor",
      },
    },
  });

  // Handle initial value
  useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      editor.commands.setContent(value);
    }
  }, [editor, value]);

  // Handle client-side only rendering
  useEffect(() => {
    setIsMounted(true);
  }, []);

  if (!isMounted) {
    return null;
  }

  return (
    <div className={cn("border rounded-md", className)}>
      <MenuBar editor={editor} />
      <EditorContent
        editor={editor}
        className="prose prose-sm max-w-none p-4 focus:outline-none min-h-[200px] rich-text-editor"
      />
      {/* @ts-ignore */}
      <style jsx global>{`
        .rich-text-editor {
          /* Basic editor styling */
          font-family: inherit;
          line-height: 1.5;
          color: inherit;
        }

        /* Headings */
        .rich-text-editor h1 {
          font-size: 1.75rem;
          font-weight: 700;
          margin-top: 1rem;
          margin-bottom: 0.5rem;
          line-height: 1.2;
        }

        .rich-text-editor h2 {
          font-size: 1.5rem;
          font-weight: 600;
          margin-top: 0.75rem;
          margin-bottom: 0.5rem;
          line-height: 1.25;
        }

        /* Lists */
        .rich-text-editor ul {
          list-style-type: disc;
          padding-left: 1.5rem;
          margin: 0.5rem 0;
        }

        .rich-text-editor ol {
          list-style-type: decimal;
          padding-left: 1.5rem;
          margin: 0.5rem 0;
        }

        .rich-text-editor li {
          margin-bottom: 0.25rem;
        }

        .rich-text-editor li p {
          margin: 0;
        }

        /* Blockquotes */
        .rich-text-editor blockquote {
          border-left: 3px solid #e2e8f0;
          padding-left: 1rem;
          margin-left: 0;
          margin-right: 0;
          font-style: italic;
          color: #4a5568;
        }

        /* Code blocks */
        .rich-text-editor pre {
          background-color: #f7fafc;
          border-radius: 0.25rem;
          padding: 0.75rem 1rem;
          overflow-x: auto;
          font-family: monospace;
          margin: 0.5rem 0;
        }

        .rich-text-editor code {
          background-color: #f7fafc;
          padding: 0.2rem 0.4rem;
          border-radius: 0.25rem;
          font-family: monospace;
          font-size: 0.875em;
        }

        /* Links */
        .rich-text-editor a {
          color: #3182ce;
          text-decoration: underline;
        }

        /* Tables */
        .rich-text-editor table {
          border-collapse: collapse;
          width: 100%;
          margin: 0.5rem 0;
          table-layout: fixed;
        }

        .rich-text-editor th {
          background-color: #f7fafc;
          font-weight: 600;
          text-align: left;
        }

        .rich-text-editor td,
        .rich-text-editor th {
          border: 1px solid #e2e8f0;
          padding: 0.5rem;
        }

        /* Text alignment */
        .rich-text-editor .is-text-aligned-left {
          text-align: left;
        }

        .rich-text-editor .is-text-aligned-center {
          text-align: center;
        }

        .rich-text-editor .is-text-aligned-right {
          text-align: right;
        }

        /* Images */
        .rich-text-editor img {
          max-width: 100%;
          height: auto;
          border-radius: 0.25rem;
        }

        /* Placeholder styling */
        .rich-text-editor p.is-editor-empty:first-child::before {
          content: attr(data-placeholder);
          float: left;
          color: #9ca3af;
          pointer-events: none;
          height: 0;
        }
      `}</style>
    </div>
  );
};

// Add a utility function for converting Markdown to HTML if needed
export const markdownToHtml = (markdown: string) => {
  // This is a placeholder - you would implement this with a library like marked or remark
  return markdown;
};

// Add a utility function for converting HTML to Markdown if needed
export const htmlToMarkdown = (html: string) => {
  // This is a placeholder - you would implement this with a library like turndown
  return html;
};
